import type { InputProps } from "@material-ui/core";
import { Box, createStyles, InputBase, makeStyles, Typography, withStyles } from "@material-ui/core";

import type { CoretexTheme } from "../theme";

interface FormInputProps extends InputProps {
  inputLabel?: string;
  errorMessage?: string;
}

const useStyles = makeStyles((theme: CoretexTheme) => ({
  label: {
    color: theme.palette.mono.primaryDark,
    fontSize: "14px",
    paddingBottom: theme.spacing(2),
  },
  errorMessage: {
    fontSize: "12px",
    color: theme.palette.colour.red,
  },
}));

const styles = (theme: CoretexTheme) => ({
  root: {
    width: "100%",
    padding: "0px",
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.mono.gray}`,
    "&:hover, &:focus": {
      border: `1px solid ${theme.palette.colour.primaryBlue}`,
    },
  },
  input: {
    padding: theme.spacing(2),
    color: theme.palette.mono.primaryDark,
    fontSize: "14px",
    minHeight: "20px",
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.mono.white,
  },
  error: {
    border: `1px solid ${theme.palette.colour.red} !important`,
    "&:hover, &:focus": {
      border: `1px solid ${theme.palette.colour.red}`,
    },
  },
  focused: {
    border: `1px solid ${theme.palette.colour.primaryBlue}`,
  },
  disabled: {
    background: theme.palette.mono.lightGray,
    border: `1px solid ${theme.palette.mono.gray}`,
    "& input": {
      border: "none",
    },
    "&:hover, &:focus": {
      border: `1px solid ${theme.palette.mono.gray}`,
      "& input": {
        border: "none",
      },
    },
  },
});

const TextField = withStyles(createStyles(styles))(InputBase);

const FormInput = ({
  inputLabel,
  errorMessage,
  required,
  error,
  disabled,
  ...inputProps
}: FormInputProps): JSX.Element => {
  const classes = useStyles();
  return (
    <Box>
      <Typography className={classes.label}>
        {required && "* "}
        {inputLabel}
      </Typography>
      <TextField error={error && !disabled} disabled={disabled} {...inputProps} />
      {error && !disabled && <Typography className={classes.errorMessage}>{errorMessage ?? "Required"}</Typography>}
    </Box>
  );
};

export default FormInput;
